import { DropdownIndicator } from '@ui/DropdownIndicator';
/**
 * Live Mapbox camera controls for the render debug panel: lens presets
 * (vertical field of view expressed as full-frame focal lengths) plus raw
 * fov / pitch / bearing / zoom sliders. Admin tooling for tuning how the
 * massing reads in 3D — nothing here is persisted.
 */
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { Button, Slider, Typography } from '@mui/material';
import { Menu } from '@ui/menus/Menu';
import type { DesignMap } from '../map';

type CameraDebugSectionProps = {
  map: DesignMap | undefined;
};

type CameraValues = {
  fov: number;
  pitch: number;
  bearing: number;
  zoom: number;
};

/** Mapbox's own default vertical field of view (0.6435 rad). */
const MAPBOX_DEFAULT_FOV = 36.87;

/** Mapbox clamps `fov` to this range (degrees). */
const MAPBOX_FOV_MIN = 0.01;
const MAPBOX_FOV_MAX = 60;

/** Full-frame sensor height (mm); focal-length equivalents assume landscape 3:2. */
const FULL_FRAME_SENSOR_HEIGHT_MM = 24;

/** Approximate full-frame focal length for a vertical fov. */
const fovToFocalLength = (fovDegrees: number): number =>
  FULL_FRAME_SENSOR_HEIGHT_MM / 2 / Math.tan((fovDegrees * Math.PI) / 360);

/** Vertical fov for a full-frame focal length. */
const focalLengthToFov = (focalLengthMm: number): number =>
  (2 * Math.atan(FULL_FRAME_SENSOR_HEIGHT_MM / 2 / focalLengthMm) * 180) /
  Math.PI;

type FovPreset = { label: string; hint?: string; fov: number };

type FovPresetGroup = { label: string; presets: FovPreset[] };

/**
 * Photographer presets are full-frame focal lengths. Mapbox caps fov at 60°
 * (~21 mm), so the wide real-estate/architecture lenses (14–17 mm) are out of
 * reach; 24 mm is the widest classic architecture lens that fits.
 */
const FOV_PRESET_GROUPS: FovPresetGroup[] = [
  {
    label: 'Lens (full frame)',
    presets: [
      {
        label: '24mm',
        hint: 'Architecture / exterior',
        fov: focalLengthToFov(24),
      },
      { label: '28mm', fov: focalLengthToFov(28) },
      { label: '35mm', hint: 'Reportage', fov: focalLengthToFov(35) },
      { label: '50mm', hint: 'Normal', fov: focalLengthToFov(50) },
      { label: '70mm', fov: focalLengthToFov(70) },
      {
        label: '85mm',
        hint: 'Detail / compression',
        fov: focalLengthToFov(85),
      },
      { label: '135mm', fov: focalLengthToFov(135) },
      { label: '200mm', fov: focalLengthToFov(200) },
    ],
  },
  {
    label: 'Defaults',
    presets: [
      { label: 'Widest', hint: 'Mapbox max', fov: MAPBOX_FOV_MAX },
      { label: 'Mapbox', hint: 'Mapbox default', fov: MAPBOX_DEFAULT_FOV },
      { label: 'Isometric', hint: 'Mapbox min', fov: MAPBOX_FOV_MIN },
    ],
  },
];

const ALL_FOV_PRESETS = FOV_PRESET_GROUPS.flatMap((group) => group.presets);

const findPreset = (fov: number): FovPreset | undefined =>
  ALL_FOV_PRESETS.find((preset) => Math.abs(preset.fov - fov) < 0.05);

const formatFov = (fov: number): string =>
  `${fov.toFixed(1)}° ≈ ${fovToFocalLength(fov).toFixed(0)}mm`;

const useStyles = makeStyles()((theme) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(0.5),
  },
  sectionLabel: {
    color: theme.palette.text.secondary,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(0.5),
  },
  rowLabel: {
    minWidth: 96,
    fontVariantNumeric: 'tabular-nums',
  },
  slider: {
    flex: 1,
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  presetItem: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    width: '100%',
  },
  presetHint: {
    color: theme.palette.text.secondary,
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  },
}));

const readCamera = (map: DesignMap | undefined): CameraValues | undefined => {
  const mapbox = map?.map;
  if (!mapbox) return undefined;
  return {
    fov: mapbox.getVerticalFieldOfView(),
    pitch: mapbox.getPitch(),
    bearing: mapbox.getBearing(),
    zoom: mapbox.getZoom(),
  };
};

export const CameraDebugSection: React.FC<CameraDebugSectionProps> = ({
  map,
}) => {
  const { classes } = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [values, setValues] = useState<CameraValues | undefined>(() =>
    readCamera(map),
  );

  // Mirror gestures (drag/scroll/rotate) into the sliders.
  useEffect(() => {
    const mapbox = map?.map;
    if (!mapbox) return;
    const sync = () => setValues(readCamera(map));
    sync();
    mapbox.on('move', sync);
    return () => {
      mapbox.off('move', sync);
    };
  }, [map]);

  const apply = useCallback(
    (next: Partial<CameraValues>) => {
      const mapbox = map?.map;
      if (!mapbox) return;
      mapbox.jumpTo(next);
      setValues(readCamera(map));
    },
    [map],
  );

  if (!values) return null;

  const activePreset = findPreset(values.fov);

  const row = (
    label: string,
    key: keyof CameraValues,
    min: number,
    max: number,
    step: number,
    format: (v: number) => string = (v) => v.toFixed(1),
  ) => (
    <div className={classes.row}>
      <Typography className={classes.rowLabel} variant="caption">
        {label} {format(values[key])}
      </Typography>
      <Slider
        className={classes.slider}
        size="small"
        min={min}
        max={max}
        step={step}
        value={values[key]}
        onChange={(_, value) => apply({ [key]: value as number })}
      />
    </div>
  );

  return (
    <Fragment>
      <div className={classes.header}>
        <Typography className={classes.sectionLabel} variant="caption">
          Camera
        </Typography>
        <Button
          variant="text"
          color="inherit"
          size="small"
          endIcon={<DropdownIndicator open={Boolean(anchorEl)} />}
          onClick={(event) => setAnchorEl(event.currentTarget)}
        >
          {activePreset ? activePreset.label : 'Custom'}
          &nbsp;
          <span className={classes.presetHint}>{formatFov(values.fov)}</span>
        </Button>
      </div>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        width={300}
      >
        {FOV_PRESET_GROUPS.map((group, index) => (
          <Fragment key={group.label}>
            {index > 0 && <Menu.Divider />}
            <Menu.Group label={group.label}>
              {group.presets.map((preset) => (
                <Menu.Item
                  key={preset.label}
                  selected={activePreset === preset}
                  onClick={() => apply({ fov: preset.fov })}
                >
                  <span className={classes.presetItem}>
                    <span>
                      {preset.label}
                      {preset.hint && (
                        <span className={classes.presetHint}>
                          {' '}
                          · {preset.hint}
                        </span>
                      )}
                    </span>
                    <span className={classes.presetHint}>
                      {formatFov(preset.fov)}
                    </span>
                  </span>
                </Menu.Item>
              ))}
            </Menu.Group>
          </Fragment>
        ))}
      </Menu>

      {row('FOV', 'fov', MAPBOX_FOV_MIN, MAPBOX_FOV_MAX, 0.1, formatFov)}
      {row('Pitch', 'pitch', 0, 85, 1, (v) => `${v.toFixed(0)}°`)}
      {row('Bearing', 'bearing', -180, 180, 1, (v) => `${v.toFixed(0)}°`)}
      {row('Zoom', 'zoom', 14, 22, 0.05, (v) => v.toFixed(2))}
    </Fragment>
  );
};
